import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Paperclip, X, Pencil, Trash2, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import useChat from '../../hooks/useChat';
import { format, isToday, isYesterday } from 'date-fns';

const AVATAR_COLORS = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
function getUserColor(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function formatMsgTime(d) { try { return format(new Date(d), 'h:mm a'); } catch { return ''; } }
function formatDateSep(d) {
  try {
    const dt = new Date(d);
    if (isToday(dt)) return 'Today';
    if (isYesterday(dt)) return 'Yesterday';
    return format(dt, 'MMM d, yyyy');
  } catch { return ''; }
}

export default function DMChat({ channelId }) {
  const { user } = useAuthStore();
  const { messages, loading, hasMore, loadMore, sendMessage, editMessage, deleteMessage, emitTyping, typingUsers, uploadAttachment } = useChat(channelId);
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoScroll && messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, autoScroll]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 60);
  }, []);

  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content && attachments.length === 0) return;
    try {
      await sendMessage({ content: content || '[Attachment]', attachments: attachments.length > 0 ? attachments : undefined });
      setInputValue('');
      setAttachments([]);
      setAutoScroll(true);
    } catch (e) { /* ignore */ }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadAttachment(file);
      setAttachments(prev => [...prev, result.attachment]);
    } catch (e) { /* ignore */ }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleEditSubmit = async () => {
    if (!editContent.trim() || !editingId) return;
    try { await editMessage(editingId, editContent.trim()); setEditingId(null); setEditContent(''); } catch (e) { /* ignore */ }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); editingId ? handleEditSubmit() : handleSend(); }
    if (e.key === 'Escape' && editingId) { setEditingId(null); setEditContent(''); }
  };

  const grouped = [];
  let lastDate = '';
  for (const msg of messages) {
    const dk = new Date(msg.created_at).toDateString();
    if (dk !== lastDate) { grouped.push({ type: 'sep', date: msg.created_at, key: 'sep-' + dk }); lastDate = dk; }
    grouped.push({ type: 'msg', ...msg, key: msg.id });
  }

  const typingNames = Object.values(typingUsers);

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
        {hasMore && messages.length > 0 && (
          <button onClick={loadMore} disabled={loading}
            className="w-full text-center text-xs text-blue-500 hover:text-blue-700 py-2 disabled:opacity-50">
            {loading ? 'Loading...' : 'Load older messages'}
          </button>
        )}
        {loading && messages.length === 0 && <div className="text-center text-xs text-gray-400 py-8">Loading...</div>}
        {!loading && messages.length === 0 && <div className="text-center text-xs text-gray-400 py-8">No messages yet</div>}

        {grouped.map(item => {
          if (item.type === 'sep') return (
            <div key={item.key} className="flex items-center gap-2 py-2">
              <div className="flex-1 border-t border-gray-100" />
              <span className="text-[10px] text-gray-400">{formatDateSep(item.date)}</span>
              <div className="flex-1 border-t border-gray-100" />
            </div>
          );

          const msg = item;
          const isMe = msg.sender_id === user?.id;
          const senderName = `${msg.sender_first || ''} ${msg.sender_last || ''}`.trim();
          const att = typeof msg.attachments === 'string' ? JSON.parse(msg.attachments || '[]') : (msg.attachments || []);

          return (
            <div key={msg.key} className={`group flex gap-2 py-1 ${isMe ? 'flex-row-reverse' : ''}`}>
              {!isMe && (
                <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[10px] font-medium flex-shrink-0"
                  style={{ backgroundColor: getUserColor(msg.sender_id) }}>
                  {(msg.sender_first || '?')[0]}{(msg.sender_last || '')[0]}
                </div>
              )}
              <div className={`max-w-[80%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                <div className={`flex items-center gap-1.5 mb-0.5 ${isMe ? 'flex-row-reverse' : ''}`}>
                  <span className="text-[11px] font-medium text-gray-600">{isMe ? 'You' : senderName}</span>
                  <span className="text-[10px] text-gray-300">{formatMsgTime(msg.created_at)}</span>
                </div>
                <div className={`relative rounded-lg px-3 py-1.5 text-sm break-words ${isMe ? 'bg-blue-50' : 'bg-gray-50'}`}>
                  {editingId === msg.id ? (
                    <div>
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)} onKeyDown={handleKeyDown}
                        className="w-full text-sm border border-gray-200 rounded p-1.5 resize-none focus:outline-none" rows={2} autoFocus />
                      <div className="flex gap-1 mt-1">
                        <button onClick={handleEditSubmit} className="text-xs text-blue-600 font-medium">Save</button>
                        <button onClick={() => { setEditingId(null); setEditContent(''); }} className="text-xs text-gray-400">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  )}
                  {msg.is_edited && !editingId && <span className="text-[10px] text-gray-400 italic ml-1">(edited)</span>}
                  {att.length > 0 && (
                    <div className="mt-1 space-y-1">
                      {att.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white border border-gray-100 rounded px-2 py-1 text-xs">
                          <Paperclip className="h-3 w-3 text-gray-400" />
                          <span className="truncate flex-1">{a.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {editingId !== msg.id && isMe && (
                    <div className="absolute top-0 left-0 -translate-x-full pl-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                      <button onClick={() => { setEditingId(msg.id); setEditContent(msg.content); }}
                        className="p-1 text-gray-300 hover:text-blue-500"><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => deleteMessage(msg.id)}
                        className="p-1 text-gray-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {typingNames.length > 0 && (
          <div className="flex items-center gap-2 py-1 px-1">
            <div className="flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-[11px] text-gray-400">{typingNames[0]} is typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Pending attachments */}
      {attachments.length > 0 && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50">
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-1 text-xs">
                <Paperclip className="h-3 w-3 text-gray-400" />
                <span className="truncate max-w-[100px]">{a.filename}</span>
                <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 px-3 py-2 bg-white">
        <div className="flex items-end gap-2">
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0 disabled:opacity-50"><Paperclip className="h-4 w-4" /></button>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />
          <textarea ref={inputRef} value={inputValue}
            onChange={e => { setInputValue(e.target.value); emitTyping(); }}
            onKeyDown={handleKeyDown} placeholder="Type a message..."
            className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-300 max-h-[80px] min-h-[36px]"
            rows={1}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }} />
          <button onClick={handleSend} disabled={!inputValue.trim() && attachments.length === 0}
            className="p-1.5 text-blue-500 hover:text-blue-600 flex-shrink-0 disabled:text-gray-300"><Send className="h-4 w-4" /></button>
        </div>
        {uploading && <span className="text-[10px] text-gray-400 mt-1">Uploading...</span>}
      </div>
    </div>
  );
}
